/**
 * PS1 マルチディスク用の .m3u ヘルパー。
 *
 * pcsx_rearmed は .m3u を読むと Disk Control で枚数切替できる。
 * 各行は同一ディレクトリ上の .cue / .bin / .iso 等への相対パス。
 */

import { basename, normalizeKey, stripBom } from './cue';

export function isM3uFilename(name: string): boolean {
  return /\.m3u$/i.test(name);
}

/** m3u 本文からエントリ（空行・コメント除外）を取り出す。 */
export function parseM3uEntries(text: string): string[] {
  const entries: string[] = [];
  const seen = new Set<string>();
  for (const raw of stripBom(text).split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const name = basename(line);
    const key = normalizeKey(name);
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push(name);
  }
  return entries;
}

export function buildM3u(entries: string[]): string {
  return entries.map((e) => basename(e)).join('\n') + '\n';
}

/** ファイル名から「Disc 1」風の短い表示名を作る。 */
export function discLabelFromFilename(filename: string, index: number): string {
  const base = basename(filename).replace(/\.[^.]+$/i, '');
  const m =
    /\((?:Disc|Disk|CD|ディスク)\s*([0-9]+)\)/i.exec(base) ??
    /(?:Disc|Disk|CD|ディスク)\s*([0-9]+)/i.exec(base) ??
    /(?:^|[_\-\s])([0-9]+)(?:\s*$)/.exec(base);
  if (m?.[1]) return `Disc ${Number(m[1])}`;
  return `Disc ${index + 1}`;
}

/**
 * 複数ディスク名から共通タイトルを推定する。
 * 「Foo (Disc 1)」「Foo (Disc 2)」→「Foo」
 */
export function commonTitleFromDiscNames(names: string[]): string {
  if (names.length === 0) return 'Multi Disc';
  const stems = names.map((n) =>
    basename(n)
      .replace(/\.[^.]+$/i, '')
      .replace(/\s*[([]?\s*(?:Disc|Disk|CD|ディスク)\s*[0-9]+\s*[)\]]?\s*$/i, '')
      .replace(/[\s_-]+$/g, '')
      .trim(),
  );
  const first = stems[0] ?? 'Multi Disc';
  if (stems.every((s) => normalizeKey(s) === normalizeKey(first))) return first || 'Multi Disc';
  // 最長共通接頭辞（単語境界寄り）
  let prefix = first;
  for (const s of stems.slice(1)) {
    let i = 0;
    const a = prefix.toLowerCase();
    const b = s.toLowerCase();
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    prefix = prefix.slice(0, i).replace(/[\s_\-（(]+$/g, '').trim();
  }
  return prefix || first || 'Multi Disc';
}
